import { CARD, HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  type CopyStrings,
  WordCopyButton,
} from "@/components/tools/vocabulary/word-parts";
import type { Idiom, IdiomTheme } from "@/lib/tool-kit/word-knowledge/idioms";

export interface IdiomCardStrings extends CopyStrings {
  themeNames: Record<IdiomTheme, string>;
  informalBadge: string;
  meaningLabel: string;
  exampleLabel: string;
  alsoLabel: string;
}

const BADGE =
  "rounded bg-primary/10 px-1.5 py-0.5 font-normal text-primary text-xs";
const BADGE_MUTED =
  "rounded bg-muted px-1.5 py-0.5 font-normal text-muted-foreground text-xs";

/** One idiom: the phrase, what it means, a sentence that uses it, and its themes. */
export function IdiomCard({
  idiom,
  strings,
  onTheme,
}: {
  idiom: Idiom;
  strings: IdiomCardStrings;
  onTheme: (theme: IdiomTheme) => void;
}) {
  return (
    <article className={`${CARD} space-y-2`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className={WIDGET_H3}>{idiom.text}</h3>
        <WordCopyButton strings={strings} word={idiom.text} />
      </div>
      <p className="text-foreground text-sm">
        <span className="font-medium">{strings.meaningLabel}</span>{" "}
        {idiom.meaning}
      </p>
      <p className="text-muted-foreground text-sm italic">
        <span className="font-medium not-italic">{strings.exampleLabel}</span>{" "}
        {idiom.example}
      </p>
      {idiom.variants.length > 0 ? (
        <p className={HINT}>
          {strings.alsoLabel} {idiom.variants.join("; ")}
        </p>
      ) : null}
      <p className="flex flex-wrap gap-2">
        {idiom.themes.map((theme) => (
          <button
            className={`${BADGE} hover:underline`}
            key={theme}
            onClick={() => onTheme(theme)}
            type="button"
          >
            {strings.themeNames[theme]}
          </button>
        ))}
        {idiom.informal ? (
          <span className={BADGE_MUTED}>{strings.informalBadge}</span>
        ) : null}
      </p>
    </article>
  );
}
